/* eslint-disable @typescript-eslint/consistent-indexed-object-style */
// store/breadcrumb.selectors.ts
import { ActivatedRouteSnapshot } from '@angular/router';


interface Breadcrumb {
  label: string;
  routerLink: string;
}

type ParamResolver = (route: ActivatedRouteSnapshot) => string | null;

/**
 * Builds the breadcrumb trail for the active route.
 *
 * `data.breadcrumb` holds a translation key. Routes that carry an entity id (see `resolverMap`)
 * are labelled with the entity name instead — those are never translated. Lazy child routes
 * inherit their parent's `data`, which is what makes them eligible for a crumb at all; a child
 * whose resolver cannot name the entity yet is skipped rather than falling back to the
 * inherited parent label.
 */
export function buildBreadcrumbs(
  route: ActivatedRouteSnapshot,
  url = '',
  breadcrumbs: Breadcrumb[] = [],
  resolverMap: { [path: string]: ParamResolver } = {},
  translate: (key: string) => string = (key) => key
): Breadcrumb[] {
  const labelKey = route.data?.['breadcrumb'];
  const path = route.routeConfig?.path;

  if (labelKey && path) {
    const segment = path.includes(':')
      ? Object.keys(route.params).reduce(
        (acc, param) => acc.replace(`:${param}`, route.params[param]),
        path
      )
      : path;

    const nextUrl = url + '/' + segment;
    const resolver = resolverMap[path];

    if (resolver) {
      const entityName = resolver(route);
      if (entityName) {
        breadcrumbs.push({ label: entityName, routerLink: nextUrl });
      }
    } else {
      let resolvedLabel = translate(labelKey);
      Object.keys(route.params).forEach(param => {
        resolvedLabel = resolvedLabel.replace(`:${param}`, route.params[param]);
      });

      breadcrumbs.push({ label: resolvedLabel, routerLink: nextUrl });
    }

    url = nextUrl;
  }

  if (route.firstChild) {
    return buildBreadcrumbs(route.firstChild, url, breadcrumbs, resolverMap, translate);
  }

  return breadcrumbs;
}
